import { findLastIndex, isEqual } from 'lodash';
import { memo } from 'react';
import styled from 'styled-components';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';

interface Props {
  readonly maxLength: number;
  readonly legs: readonly string[];
}

function LegsDescription({ maxLength, legs }: Props) {
  const areLegsValidForColumnDisplay = legs.every(isValidForDisplay);
  const displayedLegs = legs.slice(0, maxLength);

  const shouldShowEllipsis = (leg: string) => {
    const index = legs.indexOf(leg);
    const isLast = index + 1 === maxLength;
    const showEllipsis = isLast && legs.length > maxLength;

    return showEllipsis;
  };

  /**
   * If there is any leg that does not follow the known pattern,
   * just display each leg directly. One line per leg either way — the row
   * only has room for `maxLength` of them, and a wrapped line overflows into
   * the neighbouring rows.
   */
  if (!areLegsValidForColumnDisplay) {
    return (
      <LegTextLines data-testid="legs-description-lines">
        {displayedLegs.map((leg) => (
          <div key={leg}>{`${leg}${
            shouldShowEllipsis(leg) ? ELLIPSIS : ''
          }`}</div>
        ))}
      </LegTextLines>
    );
  }

  const columns = getColumnLayout(legs);
  return (
    <LegLines
      data-testid="legs-description-columns"
      numColumns={countColumns(columns)}
    >
      {displayedLegs.map((leg, legIndex) => (
        <LegLine
          key={leg}
          index={legIndex + 1}
          cells={getLegCells(getLegInfo(leg), columns)}
          showEllipsis={shouldShowEllipsis(leg)}
        />
      ))}
    </LegLines>
  );
}

const MemoizedLegsDescription = memo(
  LegsDescription,
  (prev, next) =>
    isEqual(prev.legs, next.legs) && prev.maxLength === next.maxLength,
);
export default MemoizedLegsDescription;

export const DESCRIPTION_SEPARATOR = '  ';

const ELLIPSIS = '...';

const isValidForDisplay = (leg: string) => {
  const numberColumns = leg.split(DESCRIPTION_SEPARATOR).length;
  const maxColumns = isNormalLeg(leg)
    ? MAX_COLUMNS_OPTION_LEG
    : MAX_COLUMNS_OTHER_LEG;

  return numberColumns >= MIN_COLUMNS_LEG && numberColumns <= maxColumns;
};

const isNormalLeg = (leg: string) =>
  leg.includes('Call') || leg.includes('Put');

const isFutureLeg = (leg: string) => leg.includes('Future');

/**
 * Hedge leg (`ratio  expiry  price`) is the shortest description.
 */
const MIN_COLUMNS_LEG = 3;
/**
 * `ratio  Call|Put  expiry  strike` plus the product code of a multi-product
 * structure.
 */
const MAX_COLUMNS_OPTION_LEG = 5;
/**
 * `ratio  Future  expiry` plus the product code of a multi-product structure.
 */
const MAX_COLUMNS_OTHER_LEG = 4;

/**
 * Only a multi-product structure names the product code, and only on its
 * strategy legs — so a trailing column beyond the leg's own fields is one.
 */
const hasProductCode = (leg: string) => {
  const numberColumns = leg.split(DESCRIPTION_SEPARATOR).length;
  if (isNormalLeg(leg)) return numberColumns === MAX_COLUMNS_OPTION_LEG;
  if (isFutureLeg(leg)) return numberColumns === MAX_COLUMNS_OTHER_LEG;

  return false;
};

const getLegInfo = (leg: string) => {
  const legsInfo = leg.split(DESCRIPTION_SEPARATOR);
  if (isNormalLeg(leg)) {
    const [ratio, instrument, expiry, price, productCode] = legsInfo;
    return {
      ratio,
      instrument,
      expiry,
      price,
      productCode,
      type: 'normal',
    } as const;
  }

  if (isFutureLeg(leg)) {
    const [ratio, kind, expiry, productCode] = legsInfo;
    return { ratio, kind, expiry, productCode, type: 'future' } as const;
  }

  const [ratio, expiry, price] = legsInfo;
  return { ratio, expiry, price, type: 'hedge' } as const;
};

type LegInfo = ReturnType<typeof getLegInfo>;

/**
 * Which columns the whole leg list needs. Ratio and expiry are always there;
 * the rest only exist if some leg fills them, and then every leg has to emit
 * that cell — empty when it has nothing for it — or the grid rows drift out of
 * alignment.
 */
interface ColumnLayout {
  /** `Call`/`Put`/`Future`. Hedge legs have no kind. */
  readonly kind: boolean;
  /** The option strike, or the hedge leg's price. Futures have neither. */
  readonly price: boolean;
  /** The leg's product code, named only by a multi-product structure. */
  readonly product: boolean;
}

const getColumnLayout = (legs: readonly string[]): ColumnLayout => ({
  kind: legs.some((leg) => isNormalLeg(leg) || isFutureLeg(leg)),
  price: legs.some((leg) => !isFutureLeg(leg)),
  product: legs.some(hasProductCode),
});

const ALWAYS_PRESENT_COLUMNS = 2; // ratio and expiry

const countColumns = (columns: ColumnLayout) =>
  ALWAYS_PRESENT_COLUMNS + Object.values(columns).filter(Boolean).length;

interface LegCell {
  /** Suffixed with the leg's position, e.g. `ratio-1`. */
  readonly testIdPrefix: string;
  readonly text?: string;
  /** Columns the cell covers, when it takes over one it leaves empty. */
  readonly span?: number;
}

const getKindCell = (leg: LegInfo): LegCell => {
  if (leg.type === 'normal')
    return { testIdPrefix: 'instrument', text: leg.instrument };
  if (leg.type === 'future') return { testIdPrefix: 'kind', text: leg.kind };

  return { testIdPrefix: 'empty-cell' };
};

/**
 * The cells of one leg, in column order, padded to the list's layout.
 */
const getLegCells = (leg: LegInfo, columns: ColumnLayout): LegCell[] => {
  const cells: LegCell[] = [{ testIdPrefix: 'ratio', text: leg.ratio }];

  if (columns.kind) cells.push(getKindCell(leg));

  cells.push({ testIdPrefix: 'expiry', text: leg.expiry });

  /**
   * A future leg has no strike, so rather than leave that column blank and
   * push its product code a gap away from the expiry, the code takes both
   * columns and sits right after it.
   */
  if (leg.type === 'future' && columns.price && columns.product)
    return [
      ...cells,
      { testIdPrefix: 'product', text: leg.productCode, span: 2 },
    ];

  if (columns.price)
    cells.push(
      leg.type === 'future'
        ? { testIdPrefix: 'empty-price' }
        : { testIdPrefix: 'price', text: leg.price },
    );

  if (columns.product)
    cells.push(
      leg.type === 'hedge' || leg.productCode == null
        ? { testIdPrefix: 'empty-product' }
        : { testIdPrefix: 'product', text: leg.productCode },
    );

  return cells;
};

interface LegLineProps {
  readonly index: number;
  readonly cells: readonly LegCell[];
  readonly showEllipsis: boolean;
}

function LegLine({ index, cells, showEllipsis }: LegLineProps) {
  /**
   * The ellipsis marks the truncation of the leg list, so it goes at the end
   * of the line — on the last cell this leg actually fills, not on a padding
   * cell that would leave it floating a column away.
   */
  const lastFilledCell = findLastIndex(
    cells,
    (cell) => cell.text != null && cell.text !== '',
  );

  return (
    <>
      {cells.map(({ testIdPrefix, text, span }, cellIndex) => (
        <Cell
          key={testIdPrefix}
          data-testid={`${testIdPrefix}-${index}`}
          $span={span}
        >
          {text}
          {showEllipsis && cellIndex === lastFilledCell ? ELLIPSIS : ''}
        </Cell>
      ))}
    </>
  );
}

const Cell = styled.span<{ $span?: number }>`
  ${({ $span }) => ($span != null ? `grid-column: span ${$span};` : '')}
`;

const LegTextLines = styled.div`
  white-space: nowrap;

  div {
    line-height: 1.2rem;
    font-size: 0.875rem;
  }
`;

const LegLines = styled.div<{ numColumns: number }>`
  display: inline-grid;
  grid-template-columns: repeat(${({ numColumns }) => numColumns}, auto);
  column-gap: 4px;
  white-space: nowrap;

  span {
    line-height: 1.2rem;
    font-size: 0.875rem;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    row-gap: 2px;
  }
`;
